import React, { useEffect, useMemo, useState, createContext, useContext } from 'react';

type EthereumProvider = {
  isMetaMask?: boolean;
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  on: (event: 'accountsChanged' | 'chainChanged', handler: (args: unknown) => void) => void;
  removeListener: (
    event: 'accountsChanged' | 'chainChanged',
    handler: (args: unknown) => void
  ) => void;
};

declare global {
  interface Window {
    ethereum?: EthereumProvider;
  }
}

type WalletContextType = {
  isConnected: boolean;
  address: string | null;
  isAdmin: boolean;
  chainId: string | null;
  isMetaMaskInstalled: boolean;
  error: string | null;
  balance: string | null;
  connectWallet: () => Promise<void>;
  disconnectWallet: () => void;
};
const WalletContext = createContext<WalletContextType | undefined>(undefined);
const AUTO_CONNECT_KEY = 'wallet:autoConnect';
const PRIMARY_INDEX_KEY = 'wallet:primaryIndex';
export function WalletProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [isConnected, setIsConnected] = useState(false);
  const [address, setAddress] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [chainId, setChainId] = useState<string | null>(null);
  const [isMetaMaskInstalled, setIsMetaMaskInstalled] = useState<boolean>(
    Boolean(typeof window !== 'undefined' && window.ethereum)
  );
  const [error, setError] = useState<string | null>(null);
  const [balance, setBalance] = useState<string | null>(null);

  const setConnectedAccount = (nextAddress: string | null) => {
    setAddress(nextAddress);
    setIsConnected(Boolean(nextAddress));
    setIsAdmin(false);
  };

  const loadBalance = async (account: string | null) => {
    if (!account || !window.ethereum) {
      setBalance(null);
      return;
    }
    try {
      const raw = (await window.ethereum.request({
        method: 'eth_getBalance',
        params: [account, 'latest']
      })) as string;
      const wei = BigInt(raw);
      const etherIntPart = wei / 10n ** 18n;
      const etherFracPart = (wei % 10n ** 18n) / 10n ** 14n; // 4 decimals
      const padded = etherFracPart.toString().padStart(4, '0');
      setBalance(`${etherIntPart.toString()}.${padded}`);
    } catch {
      setBalance(null);
      setError('Failed to load balance.');
    }
  };

  const connectWallet = async () => {
    setError(null);
    if (!window.ethereum) {
      setIsMetaMaskInstalled(false);
      setError('MetaMask is not installed.');
      return;
    }
    try {
      setIsMetaMaskInstalled(true);
      const accounts = (await window.ethereum.request({
        method: 'eth_requestAccounts'
      })) as string[];
      const storedIndex = localStorage.getItem(PRIMARY_INDEX_KEY);
      const preferredIndex = storedIndex ? Number(storedIndex) : 0;
      const primary = accounts?.[preferredIndex] ?? accounts?.[0] ?? null;
      setConnectedAccount(primary);
      localStorage.setItem(AUTO_CONNECT_KEY, 'true');
      const nextChainId = (await window.ethereum.request({
        method: 'eth_chainId'
      })) as string;
      setChainId(nextChainId ?? null);
      await loadBalance(primary);
    } catch (err) {
      setError('Failed to connect to MetaMask.');
    }
  };
  const disconnectWallet = () => {
    setIsConnected(false);
    setAddress(null);
    setIsAdmin(false);
    setChainId(null);
    setError(null);
    setBalance(null);
    localStorage.setItem(AUTO_CONNECT_KEY, 'false');
  };

  useEffect(() => {
    const provider = window.ethereum;
    setIsMetaMaskInstalled(Boolean(provider));
    if (!provider) {
      return;
    }

    const handleAccountsChanged = (accounts: unknown) => {
      const nextAccounts = Array.isArray(accounts) ? (accounts as string[]) : [];
      const storedIndex = localStorage.getItem(PRIMARY_INDEX_KEY);
      const preferredIndex = storedIndex ? Number(storedIndex) : 0;
      const next = nextAccounts[preferredIndex] ?? nextAccounts[0] ?? null;
      setConnectedAccount(next);
      if (!next) {
        setChainId(null);
        setBalance(null);
        return;
      }
      provider
        .request({ method: 'eth_chainId' })
        .then(chain => {
          if (typeof chain === 'string') setChainId(chain);
        })
        .catch(() => {
          setChainId(null);
        });
      loadBalance(next);
    };

    const handleChainChanged = (nextChainId: unknown) => {
      if (typeof nextChainId === 'string') {
        setChainId(nextChainId);
        provider
          .request({ method: 'eth_accounts' })
          .then(accounts => {
            const nextAccounts = Array.isArray(accounts) ? (accounts as string[]) : [];
            const storedIndex = localStorage.getItem(PRIMARY_INDEX_KEY);
            const preferredIndex = storedIndex ? Number(storedIndex) : 0;
            const next = nextAccounts[preferredIndex] ?? nextAccounts[0] ?? null;
            setConnectedAccount(next);
            loadBalance(next);
          })
          .catch(() => {
            setBalance(null);
            setConnectedAccount(null);
          });
      }
    };

    provider.on('accountsChanged', handleAccountsChanged);
    provider.on('chainChanged', handleChainChanged);

    const shouldAutoConnect = localStorage.getItem(AUTO_CONNECT_KEY) === 'true';
    if (shouldAutoConnect) {
      provider
        .request({ method: 'eth_accounts' })
        .then(accounts => {
          const nextAccounts = Array.isArray(accounts) ? (accounts as string[]) : [];
          const storedIndex = localStorage.getItem(PRIMARY_INDEX_KEY);
          const preferredIndex = storedIndex ? Number(storedIndex) : 0;
          const next = nextAccounts[preferredIndex] ?? nextAccounts[0] ?? null;
          setConnectedAccount(next);
          return provider.request({ method: 'eth_chainId' }).then(chain => ({ chain, next }));
        })
        .then(({ chain, next }) => {
          if (typeof chain === 'string') {
            setChainId(chain);
          }
          loadBalance(next);
        })
        .catch(() => {
          setError('Failed to read wallet state.');
        });
    }

    return () => {
      provider.removeListener('accountsChanged', handleAccountsChanged);
      provider.removeListener('chainChanged', handleChainChanged);
    };
  }, []);

  const value = useMemo(
    () => ({
      isConnected,
      address,
      isAdmin,
      chainId,
      isMetaMaskInstalled,
      error,
      balance,
      connectWallet,
      disconnectWallet
    }),
    [isConnected, address, isAdmin, chainId, isMetaMaskInstalled, error, balance]
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}
export const useWallet = () => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};
